import { ClientLoggingService } from '../../../shared/client-logging.service';
import { Component, OnInit, inject } from '@angular/core';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ProjectRoutingService } from '../../../shared/project-routing.service';
import { FirebaseAuthService } from '../../../shared/firebase-auth.service';
import { EnvironmentService } from '../../../shared/environment.service';
import { LoadingComponent } from '../../shared/ui/loading/loading.component';
import { filter, take } from 'rxjs/operators';

/** Post-login landing page that lists the user's projects and provides entry points to create or select a project. */
@Component({
  selector: 'app-landing-page',
  imports: [MatProgressSpinnerModule, MatButtonModule, MatIconModule, LoadingComponent],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.scss'],
})
export class LandingPageComponent implements OnInit {
  private projectRoutingService = inject(ProjectRoutingService);
  private firebaseAuthService = inject(FirebaseAuthService);
  private environmentService = inject(EnvironmentService);

  loading = true;
  error: string | null = null;

  ngOnInit(): void {
    this.initializeRouting();
  }

  private async initializeRouting(): Promise<void> {
    try {
      // Wait for authentication to complete
      await this.waitForAuth();

      // Issue #304: Check unauthorized state first before any UI operations
      const unauthorized = await this.firebaseAuthService.unauthorized$.pipe(take(1)).toPromise();
      if (unauthorized) {
        ClientLoggingService.log('INFO', '[LandingPageComponent] User is unauthorized, skipping project routing');
        this.loading = false;
        return;
      }

      // Trigger authorization check explicitly to ensure any server config errors are detected
      // This will set the authorization error if there are issues
      await this.triggerAuthorizationCheck();

      // Check if there's an authorization error (server config issue)
      // If so, don't proceed with project routing - the error page will be shown
      const hasAuthError = await this.checkForAuthorizationError();
      if (hasAuthError) {
        ClientLoggingService.log(
          'INFO',
          '[LandingPageComponent] Authorization error detected, skipping project routing'
        );
        this.loading = false;
        return;
      }

      // Handle project routing only if user is properly authorized
      await this.projectRoutingService.handleProjectRouting();
    } catch (error: any) {
      ClientLoggingService.log('ERROR', '[LandingPageComponent] Error during initialization:', error);

      // Issue #304: If user is unauthorized, don't show generic error, ensure redirect
      if (error.message === 'User is not authorized') {
        ClientLoggingService.log(
          'INFO',
          '[LandingPageComponent] User unauthorized, redirecting to unauthorized page...'
        );
        // Note: FirebaseAuthService should have already triggered navigation to unauthorized page,
        // but we return here to prevent the generic error from showing
        this.loading = false;
        return;
      }

      const isDev = this.environmentService.isDevelopment();
      ClientLoggingService.log('INFO', '[LandingPageComponent] isDevelopment():', isDev);

      if (isDev) {
        this.error =
          'Development Mode: The mock user (dev@example.com) does not have permissions to access projects. ' +
          'This is expected in local development. In production, real authenticated users will have proper permissions.';
      } else {
        this.error = 'Failed to load projects. Please try again.';
      }

      this.loading = false;
    }
  }

  private waitForAuth(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Wait for loading to complete (filter + take(1) auto-completes, no manual unsubscribe needed)
      this.firebaseAuthService.loading$
        .pipe(
          filter(loading => !loading),
          take(1)
        )
        .subscribe(() => {
          // Now check the current auth state
          this.firebaseAuthService.user$.pipe(take(1)).subscribe(user => {
            if (user) {
              resolve();
            } else {
              this.firebaseAuthService.unauthorized$.pipe(take(1)).subscribe(unauthorized => {
                if (unauthorized) {
                  reject(new Error('User is not authorized'));
                } else {
                  this.firebaseAuthService.needsSignIn$.pipe(take(1)).subscribe(needsSignIn => {
                    if (needsSignIn) {
                      reject(new Error('User needs to sign in'));
                    } else {
                      // Auth state not yet determined, wait a bit
                      setTimeout(() => this.waitForAuth().then(resolve).catch(reject), 100);
                    }
                  });
                }
              });
            }
          });
        });
    });
  }

  /**
   * Trigger the authorization check by calling isAllowListed
   * This ensures that any server configuration errors are detected early
   */
  private async triggerAuthorizationCheck(): Promise<void> {
    try {
      const user = await this.firebaseAuthService.user$.pipe(take(1)).toPromise();
      if (user?.email) {
        ClientLoggingService.log('INFO', '[LandingPageComponent] Triggering authorization check for:', user.email);
        // This will trigger isAllowListed() and set authorization errors if needed
        await this.firebaseAuthService.isAllowListed(user.email);
      }
    } catch (error) {
      ClientLoggingService.log('INFO', '[LandingPageComponent] Authorization check completed (error expected):', error);
      // Errors are expected and handled by isAllowListed method
    }
  }

  /**
   * Check if there's an authorization error (server configuration error)
   * Returns true if there's an error, false if user is properly authorized
   */
  private async checkForAuthorizationError(): Promise<boolean> {
    return new Promise(resolve => {
      this.firebaseAuthService.authorizationError$.pipe(take(1)).subscribe(authError => {
        const hasError = authError !== null;
        ClientLoggingService.log(
          'INFO',
          '[LandingPageComponent] Authorization error check:',
          hasError ? authError : 'No error'
        );
        resolve(hasError);
      });
    });
  }

  retry(): void {
    this.loading = true;
    this.error = null;
    this.initializeRouting();
  }
}
